import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

const inactive = { phase: 'disabled', dirty: false };
const recoveryError = 'A saved change could not be adopted because this view changed. Your edits are still here. Export them before reloading the saved workspace.';

export function useLocalSnapshotPersistence({ sessionRef, snapshot, sessionMode, localSpace }) {
  const [status, setStatus] = useState(() => sessionRef.current?.status() || inactive);
  const mounted = useRef(true);
  const publish = useCallback(() => {
    if (!mounted.current) return;
    const next = sessionRef.current?.status() || inactive;
    setStatus(current => JSON.stringify(current) === JSON.stringify(next) ? current : next);
  }, [sessionRef]);
  const flush = useCallback(async (owner = sessionRef.current, ticket) => {
    if (!mounted.current || !owner || owner !== sessionRef.current) return { ok: false, reason: 'inactive-view' };
    const result = await owner.save(ticket);
    if (owner === sessionRef.current) publish();
    return result;
  }, [publish, sessionRef]);

  useLayoutEffect(() => {
    const owner = sessionRef.current;
    if (sessionMode !== 'local') { owner?.retire(); sessionRef.current = null; publish(); return; }
    if (!owner || owner.status().space !== localSpace) { owner?.retire(); publish(); return; }
    owner.replace(snapshot);
    publish();
  }, [localSpace, publish, sessionMode, sessionRef, snapshot]);

  useEffect(() => {
    const owner = sessionRef.current;
    if (sessionMode !== 'local' || !owner || owner.status().space !== localSpace) return undefined;
    const ticket = owner.capture();
    const timer = window.setTimeout(function flushLocalSnapshot() { return flush(owner, ticket); }, 250);
    return () => window.clearTimeout(timer);
  }, [flush, localSpace, sessionMode, sessionRef, snapshot]);

  useEffect(() => {
    const owner = sessionRef.current;
    if (sessionMode !== 'local' || !owner) return undefined;
    const save = () => { void flush(owner); };
    const hidden = () => { if (document.visibilityState === 'hidden') save(); };
    const changed = event => {
      if (owner !== sessionRef.current || (event.storageArea && event.storageArea !== window.localStorage)) return;
      owner.observeStorageChange(event.key); publish();
    };
    window.addEventListener('beforeunload', save);
    window.addEventListener('pagehide', save);
    document.addEventListener('visibilitychange', hidden);
    window.addEventListener('storage', changed);
    return () => {
      window.removeEventListener('beforeunload', save);
      window.removeEventListener('pagehide', save);
      document.removeEventListener('visibilitychange', hidden);
      window.removeEventListener('storage', changed);
    };
  }, [flush, localSpace, publish, sessionMode, sessionRef]);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      // StrictMode replays setup immediately; a real unmount retires the owner.
      queueMicrotask(() => { if (!mounted.current) sessionRef.current?.retire(); });
    };
  }, [sessionRef]);

  return {
    status,
    flush: () => flush(),
    beginAtomic: () => sessionRef.current.beginAtomic(),
    adopt(result, apply, field = 'data') {
      const adopted = sessionRef.current?.adoptAtomic(result.localCommit);
      publish();
      if (!adopted?.ok) return { ok: false, committed: true, error: recoveryError };
      apply(adopted.data);
      return { ...result, [field]: adopted.data };
    },
    install(owner) { sessionRef.current?.retire(); sessionRef.current = owner; publish(); },
    async leave() {
      const owner = sessionRef.current;
      if (!owner) return true;
      await flush(owner);
      return owner === sessionRef.current && owner.status().phase === 'saved' && !owner.status().dirty;
    },
    retire() { sessionRef.current?.retire(); publish(); },
    discardAndReload() {
      // Retire BEFORE navigation can dispatch pagehide/beforeunload.
      sessionRef.current?.retire();
      window.location.reload();
    },
  };
}
